(() => {
  const KLAVIYO_REVISION = "2026-07-15";
  const strings = window.themeVariables?.strings?.backInStock || {};

  function resolveCompanyId() {
    const fromSettings = window.themeVariables?.settings?.klaviyoPublicApiKey?.trim();

    if (fromSettings) {
      return fromSettings;
    }

    const klaviyoScript = [...document.scripts].find((script) => {
      return script.src.includes("klaviyo") && script.src.includes("company_id=");
    });

    if (!klaviyoScript) {
      return "";
    }

    try {
      return new URL(klaviyoScript.src).searchParams.get("company_id") || "";
    } catch {
      return "";
    }
  }

  function klaviyoHeaders() {
    return {
      revision: KLAVIYO_REVISION,
      "content-type": "application/vnd.api+json",
      accept: "application/vnd.api+json"
    };
  }

  async function klaviyoPost(path, companyId, body) {
    const response = await fetch(
      `https://a.klaviyo.com/client/${path}?company_id=${encodeURIComponent(companyId)}`,
      {
        method: "POST",
        headers: klaviyoHeaders(),
        body: JSON.stringify(body)
      }
    );

    if (!response.ok) {
      throw new Error(`Klaviyo ${path} request failed`);
    }

    return response;
  }

  class BackInStockSignup extends HTMLElement {
    connectedCallback() {
      this.formId = this.getAttribute("form");
      this.listId = this.getAttribute("list")?.trim() || window.themeVariables?.settings?.klaviyoEmailListId?.trim() || "";
      this.openButton = this.querySelector(":scope > button");
      this.modal = this.querySelector("[data-back-in-stock-modal]");
      this.emailInput = this.querySelector("[data-back-in-stock-email]");
      this.marketingInput = this.querySelector("[data-back-in-stock-marketing]");
      this.fields = this.querySelector("[data-back-in-stock-fields]");
      this.submitButton = this.querySelector("[data-back-in-stock-fields] button[is='custom-button'], [data-back-in-stock-fields] button");
      this.successMessage = this.querySelector("[data-back-in-stock-success]");
      this.errorMessage = this.querySelector("[data-back-in-stock-error]");
      this.closeControls = this.querySelectorAll("[data-back-in-stock-close]");

      this.openButton?.addEventListener("click", this.#open);
      this.openButton?.setAttribute("aria-haspopup", "dialog");
      this.openButton?.setAttribute("aria-expanded", "false");
      this.submitButton?.addEventListener("click", this.#onSubmit);
      this.emailInput?.addEventListener("keydown", this.#onKeydown);
      this.closeControls.forEach((control) => control.addEventListener("click", this.#close));
      document.addEventListener("keydown", this.#onDocumentKeydown);
    }

    disconnectedCallback() {
      this.openButton?.removeEventListener("click", this.#open);
      this.submitButton?.removeEventListener("click", this.#onSubmit);
      this.emailInput?.removeEventListener("keydown", this.#onKeydown);
      this.closeControls.forEach((control) => control.removeEventListener("click", this.#close));
      document.removeEventListener("keydown", this.#onDocumentKeydown);
      this.#unlock();
    }

    #onDocumentKeydown = (event) => {
      if (event.key === "Escape" && this.#isOpen()) {
        this.#close();
      }
    };

    #onKeydown = (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        this.#onSubmit(event);
      }
    };

    #isOpen() {
      return this.modal && !this.modal.hidden;
    }

    #open = (event) => {
      event?.preventDefault();
      if (!this.modal) {
        return;
      }

      this.modal.hidden = false;
      document.documentElement.classList.add("lock");
      this.openButton?.setAttribute("aria-expanded", "true");
      this.emailInput?.focus();
    };

    #close = (event) => {
      event?.preventDefault();
      if (!this.modal) {
        return;
      }

      this.modal.hidden = true;
      this.#unlock();
      this.openButton?.setAttribute("aria-expanded", "false");
      this.openButton?.focus();
    };

    #unlock() {
      document.documentElement.classList.remove("lock");
    }

    #onSubmit = async (event) => {
      event.preventDefault();
      this.#setError("");

      if (!this.emailInput?.reportValidity()) {
        this.emailInput?.focus();
        return;
      }

      const variantId = this.#getVariantId();
      const companyId = resolveCompanyId();
      const email = this.emailInput.value.trim();

      if (!variantId) {
        this.#setError(strings.missingVariant || "Select a variant to get notified.");
        return;
      }

      if (!companyId) {
        this.#setError(strings.missingAccount || "Back in stock signup is not configured.");
        return;
      }

      this.submitButton?.setAttribute("aria-busy", "true");
      if (this.submitButton) {
        this.submitButton.disabled = true;
      }

      try {
        await klaviyoPost("back-in-stock-subscriptions", companyId, {
          data: {
            type: "back-in-stock-subscription",
            attributes: {
              channels: ["EMAIL"],
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email
                  }
                }
              }
            },
            relationships: {
              variant: {
                data: {
                  type: "catalog-variant",
                  id: `$shopify:::$default:::${variantId}`
                }
              }
            }
          }
        });

        if (this.marketingInput?.checked) {
          await this.#subscribeToEmailMarketing(companyId, email);
        }

        this.#setSuccess();
      } catch (error) {
        this.#setError(strings.error || "Something went wrong. Please try again.");
        if (this.submitButton) {
          this.submitButton.disabled = false;
        }
      } finally {
        this.submitButton?.removeAttribute("aria-busy");
      }
    };

    #getVariantId() {
      const form = document.forms[this.formId];
      return form?.querySelector('[name="id"]')?.value || "";
    }

    async #subscribeToEmailMarketing(companyId, email) {
      await klaviyoPost("subscriptions", companyId, {
        data: {
          type: "subscription",
          attributes: {
            custom_source: "Featured Product Back in Stock",
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email,
                  subscriptions: {
                    email: {
                      marketing: {
                        consent: "SUBSCRIBED"
                      }
                    }
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: "list",
                id: this.listId
              }
            }
          }
        }
      });
    }

    #setSuccess() {
      if (this.fields) {
        this.fields.hidden = true;
      }

      if (this.successMessage) {
        this.successMessage.hidden = false;
        this.successMessage.focus?.();
      }
    }

    #setError(message) {
      if (!this.errorMessage) {
        return;
      }

      this.errorMessage.hidden = !message;
      this.errorMessage.textContent = message;
    }
  }

  if (!window.customElements.get("back-in-stock-signup")) {
    window.customElements.define("back-in-stock-signup", BackInStockSignup);
  }
})();
